import { View } from 'backbone.marionette';
import template from './index.jst';
import './index.styl';
import { User } from "../../../models";
import { Player } from "../../../../../core/models";
import { Preloader } from "../../../utils";
import _ from 'lodash';
import Radio from 'backbone.radio';
import MatchUpPlayersByMatching from './MatchUpPlayersByMatching';
import MatchUpPlayersByPosition from "./MatchUpPlayersByPosition";
import MatchUpChart from './MatchUpChart';
import Backbone from 'backbone';
import { redirectTo } from '../../../../../core/utils';

const matchUpChannel = Radio.channel('match_up');

export class MatchUpLayout extends View {
	get template() {
		return template;
	}

	regions() {
		return {
			users: '.js-matchup-users',
			players_by_matching: '.js-players-by-matching',
			players_by_position: '.js-players-by-position',
			chart: '.js-chart'
		}
	}

	ui() {
		return {
			change_players_by_matching: '.js-change-players-by-matching',
			change_players_by_position: '.js-change-players-by-position'
		}
	}

	events() {
		return {
			'click @ui.change_players_by_matching': 'changeTabByMatching',
			'click @ui.change_players_by_position': 'changeTabByPosition'
		}
	}

	modelEvents() {
		return {
			'league:query:not:found': 'redirectToMyLeague',
		};
	}

	async initialize() {
		this.liveScoresIntervalId = null;

		this.model.set({
			selected_players_view: 'players_by_position',
			positions: this.getOption('positions').by_id,
		});

		this.league_id = this.getOption('league_id');
		this.home_team_id = this.getOption('home_team_id');
		this.away_team_id = this.getOption('away_team_id');
		this.round_id = this.getOption('round_id');

		this.rounds = this.getOption('rounds');
		this.squads = this.getOption('squads');
		this.players = this.getOption('players');
		this.positions = this.getOption('positions');
		this.positions_by_id = this.positions.by_id;

		this.league = this.getOption('league');
		this.home_team = this.getOption('home_team').setShowUrl();
		this.away_team = this.getOption('away_team').setShowUrl();

		if (User.has_assistant_coach) {
			this.coach = this.getOption('coach');
		}

		this.setListeners()
			.setLayout();

		await this.getData();
	}

	setListeners() {
		this.listenTo(this, 'data:requested', this.onDataRequested);
		this.listenTo(this, 'get:teams', this.getTeams);

		this.listenTo(matchUpChannel, 'fixture:item:changed', this.onFixtureItemChanged);
		this.listenTo(matchUpChannel, 'round:changed', this.onRoundChanged);

		return this;
	}

	setLayout() {
		if (!this.getRegion('chart').hasView()) {
			this.showChildView('chart', new MatchUpChart());
		}
		if (!this.getRegion('players_by_matching').hasView()) {
			this.showChildView('players_by_matching', new MatchUpPlayersByMatching({
				players: this.players,
				rounds: this.rounds,
				round_id:  this.round_id
			}));
		}

		if (!this.getRegion('players_by_position').hasView()) {
			this.showChildView('players_by_position', new MatchUpPlayersByPosition({
				players: this.players,
				rounds: this.rounds,
				round_id:  this.round_id
			}));
		}

		return this;
	}

	async getData() {
		Preloader.show();

		const requests = _.invokeMap(
			_.pick(this, 'players', 'squads', 'rounds', 'venues', 'coach'),
			'fetch'
		);

		requests.push(
			this.league.setShowUrl().fetch({
				data: { id: this.league_id }
			}),
			this.home_team.fetch({
				data: { id: this.home_team_id, round: this.round_id }
			}),
			this.away_team.fetch({
				data: { id: this.away_team_id, round: this.round_id }
			})
		);

		await Promise.all(requests);

		Preloader.hide();

		this.trigger('data:requested');
	}


	async getTeams() {

		const requests = [
			this.home_team.fetch({
				data: { id: this.home_team_id, round: this.round_id }
			}),
			this.away_team.fetch({
				data: { id: this.away_team_id, round: this.round_id }
			})
		];

		await Promise.all(requests);


		this.trigger('data:requested');
	}

	onDataRequested() {
		this.checkCurrentUser();
		this.setSelectedRound();

		const is_first_round = this.selected_round.get('id') === 1,
			is_first_complete = this.selected_round.get('status') !== 'complete';

		if (is_first_round && is_first_complete) {
			this.$el.hide();
			matchUpChannel.trigger('preseason');
			return false;
		}

		this.checkRoundStatus()
			.runCollectionMethods()
			.runModelsMethods()
			.initGraph()
			.initPlayers();

		matchUpChannel.trigger('data:requested', {
			league: this.league,
			matchup: this.model.toJSON(),
			is_match_active: this.selected_round.is_match_active,
			round_id: parseInt(this.round_id, 10)
		});

		Preloader.hide();
	}

	checkCurrentUser() {
		if( !this.league
			.checkLeagueMember()
		) {
			this.redirectToMyLeague();
		}
	}

	// onTeamsRequested() {
	// 	this.runCollectionMethods()
	// 		.runModelsMethods()
	// 		.initPlayers();
	//
	// 	matchUpChannel.trigger('data:requested', {
	// 		league: this.league,
	// 		matchup: this.model.toJSON(),
	// 		is_match_active: this.selected_round.is_match_active,
	// 		round_id: parseInt(this.round_id, 10)
	// 	});
	//
	// 	Preloader.hide()
	// }

	setSelectedRound() {
		this.selected_round = this.rounds.get(this.round_id);

		return this;
	}

	checkRoundStatus() {
		if (this.selected_round.is_active) {
			this.subscribeToLiveUpdates();
		}
		else if (this.liveScoresIntervalId) {
			this.unsubscribeFromLiveUpdates();
		}
		return this;
	}

	subscribeToLiveUpdates() {
		clearTimeout(this.liveScoresIntervalId);

		const { rounds, players } = this.options;

		let time = this.selected_round.active_match ? 1 : 5;

		this.liveScoresIntervalId = setTimeout(async () => {
			const requests = _.invokeMap([rounds, players], 'fetch');

			await Promise.all(requests);

			this.trigger('data:requested');
		}, time * 1000 * 60);

	}

	unsubscribeFromLiveUpdates() {
		clearTimeout(this.liveScoresIntervalId);
	}

	runCollectionMethods() {
		this.setPlayersData();

		return this;
	}

	runModelsMethods() {
		const home_team = this.home_team,
			away_team = this.away_team;

		this.expandTeam(home_team);
		this.expandTeam(away_team);

		this.model
			.setMatchingPlayers({ home_team, away_team })
			.setUniquePlayers({ home_team, away_team });

		this.league
			.set({ rounds_collection: this.rounds })
			.setAvatar()
			.setFixtureItem(
				{
					round_id: this.round_id,
					home_team_id: +this.home_team_id,
					away_team_id: +this.away_team_id,
				}
			)
			.getFixtureRounds({ rounds: this.rounds });

		this.setMatchUpFixture();

		return this;
	}

	setMatchUpFixture() {
		this.model.setMatchUpFixture({
			league: this.league,
			round_id: this.round_id,
			home_team: this.home_team,
			away_team: this.away_team,
			selected_round: this.selected_round,
			players: this.players
		});

		return this;
	}

	setPlayersData() {
		const
			round = this.selected_round,
			squads = this.squads.toJSON(),
			positions_by_id = this.positions.by_id,
			squad_by_id = _.keyBy(squads, 'id'),
			coach = User.has_assistant_coach ? this.coach.toJSON() : false,
			actual_round_id = round.get('id'),
			bye_rounds = this.rounds
				.filter(
					round => round.get('is_bye') && round.get('status')==='scheduled'
				).map(round => round.toJSON());


		this.players.each(player => {
			player
				.setPositionNames({ positions_by_id })
				.setSquadName({ squad_by_id })
				.setOpponent({ squad_by_id, round: round.toJSON() })
				.setCostChanges({ round_id: this.round_id })
				.setStatsPointsLastRound({
					is_preseason: false, rounds: this.rounds.toJSON(),
					round_id: this.round_id-1,
					leader_id: null
				});

		
			player
				.setCoachStats({
					coach,
					actual_round_id,
				})
				.setByRound({ bye_rounds });
			let stats = player.get('stats'),
				proj_score = _.result(stats, `proj_scores[${actual_round_id}]`, 0);
			player.set({
				stats: {
					...stats,
					proj_score
				}
			});
			
		});

		return this;
	}

	expandTeam(team) {
		let team_players = this.getTeamPlayers(team),
			args = { team, players: team_players, positions: this.positions_by_id },
			lineup_players = this.model.getLineupPlayers(args),
			proj_score = false,
			is_round_complete = this.selected_round.is_complete,
			is_active_round = this.selected_round.is_active,
			is_matches_completed = this.selected_round.is_matches_complete,
			is_full_lockout = this.selected_round.is_full_lockout
		;

		const { team_score, not_counted_players } = team.getTeamScore({
			players_by_id: team_players, is_full_lockout: this.selected_round.is_full_lockout
		});

		if (User.has_assistant_coach && !is_round_complete) {
			if (is_full_lockout) {
				proj_score = team_score;
			}
			else {
				proj_score = team.getProjScore(
					{ players_by_id: team_players, is_active_round, is_matches_completed }
				);
			}
		}

		team.set({
			team_players,
			lineup_players,
			team_score,
			proj_score,
			emergency_ids: team.emergency_players_id,
			not_counted_players
		}, { silent: true });
	}

	getTeamPlayers(team) {
		const
			{ captain, vice_captain } = team.get('lineup'),
			players = this.players,
			cap_player = this.players.get(captain),
			rounds = this.rounds.toJSON(),
			selected_round = this.selected_round,
			round = this.selected_round.toJSON(),
			is_cap_scored = captain && cap_player.isPlayerScored({ round }),
			is_cap_played = captain && cap_player.alreadyPlayed({ round }),
			is_preseason = this.rounds.is_preseason,
			is_round_complete = this.selected_round.is_complete,
			actual_round_id = round.id,
			is_cap_proj = captain &&
				_.result(cap_player.toJSON(), `stats.proj_scores.${round.id}`, false),
			proj_leader_id = is_cap_proj ? captain : vice_captain
		;

		let leader_id = is_cap_scored ? captain : vice_captain;

		if (!is_cap_played) {
			leader_id = captain;
		}

		return _.keyBy(team.team_players_id.map(function(id) {
			const player = new Player(players.get(id).toJSON());

			if (User.has_assistant_coach && !is_round_complete && proj_leader_id === id) {
				let stats = player.get('stats'),
					proj_score = _.result(stats, `proj_scores.${actual_round_id}`, 0) * 2;

				player.set({
					stats: {
						...stats,
						proj_score
					}
				});
			}

			return player.setStatsPointsLastRound({
				is_preseason, rounds,
				round_id: actual_round_id,
				leader_id: !selected_round.is_scheduled ? leader_id : null
			}).toJSON();
		}), 'id');
	}

	initGraph() {

		const child = this.getChildView('chart');

		child.init({
			squads: this.squads,
			selected_round: this.selected_round,
			...this.model.toJSON()
		});

		return this;
	}

	initPlayers() {
		this.getChildView('players_by_matching').$el.addClass('hidden');
		this.getChildView('players_by_position').$el.addClass('hidden');
		const child = this.getChildView(this.model.get('selected_players_view'));

		child.$el.removeClass('hidden');

		child.init({
			selected_round: this.selected_round,
			...this.model.toJSON()
		});
		return this;

	}

	onFixtureItemChanged(index) {
		Preloader.show();

		const { first_opponent, second_opponent } = this.league.get('fixture_item')[index];
		this.home_team_id = first_opponent.id;
		this.away_team_id = second_opponent.id;

		this.changeSelectedFixture();

		this.trigger('get:teams');
	}

	onRoundChanged(round_id) {
		Preloader.show();

		if( this.league.isFinalRound( round_id ) ) {
			Preloader.hide();
			// TODO:(Dmitry) Delete when you working on the finals
			$('.container').remove();
			matchUpChannel.trigger('final');
		}
		else {
			let team_id = this.league.get('team_id'),
				fixture_arr = this.league.getFixtureById({
					round_id,
					team_id
				})
			;

			this.home_team_id = fixture_arr[0];
			this.away_team_id = fixture_arr[1];
			this.round_id = round_id;
			this.setSelectedRound();

			this.changeSelectedFixture();

			this.trigger('get:teams');
		}
	}

	changeSelectedFixture() {
		matchUpChannel.trigger('reset:chart');
		const fixture_params = `${this.home_team_id}/${this.away_team_id}/${this.round_id}`,
			href = `/fantasy/league/${this.league_id}/matchup/${fixture_params}`;

		Backbone.history.navigate(href);

		return this;
	}

	changeTabByMatching() {
		this.changeTab('players_by_matching');

	}
	changeTabByPosition() {
		this.changeTab('players_by_position');
	}

	changeTab(selected_players_view) {
		if (this.model.get('selected_players_view') === selected_players_view) {
			return this;
		}

		Preloader.show();
		this.model.set({ selected_players_view });

		this.toggleTabClasses()
			.initPlayers();

		Preloader.hide();
	}

	toggleTabClasses() {

		this.getUI('change_players_by_matching').toggleClass('selected');
		this.getUI('change_players_by_position').toggleClass('selected');

		return this;
	}

	redirectToMyLeague() {
		redirectTo( '/fantasy/leagues' );
	}
}

export default MatchUpLayout;